<template>
    <div>
        <div v-if="hasSocialiteProviders" class="mb-6">
            <BaseButton
                class="py-2 pl-12 mb-4 font-medium normal-case"
                :class="providerButtonClass(provider.toLowerCase())"
                secondary
                @click.native="processSocialRequest(provider.toLowerCase())"
                v-for="provider in socialiteProviders"
                :key="provider.id">
                    <span>{{ _(`${provider.toLowerCase()}_sso_button`) }}</span>
            </BaseButton>
        </div>
         <Portal to="spinner">
            <div v-if="loading" class="fixed inset-0 flex flex-col items-center justify-center p-8 bg-background-80">
                <div class="mb-20">
                    <BaseSpinner/>
                    <a href="" @click.prevent="stopSingleSignOn()" class="text-highlight-100">{{ _(`sso_cancel`) }}</a>
                </div>
            </div>
        </Portal>
    </div>
</template>
<script>
import { mapGetters } from 'vuex'
import BaseButton from '@/components/Base/BaseButton.vue'
import BaseSpinner from '@/components/Base/BaseSpinner.vue'
import SocialiteService from '@/services/SocialiteService'

export default {
    components: {
        BaseButton,
        BaseSpinner,
    },
    data() {
        return {
            socialiteService: new SocialiteService(),
            socialiteProviders: [],
            ssoWindow: null,
            ssoLoop: null,
            loading: false,
        }
    },
    computed: {
        ...mapGetters({
            _: 'localisation/translate',
            params: 'settings/get',
            device: 'device/getDevice',
            authenticated: 'user/authenticated',
            previousUrl: 'route/getPreviousUrl',
            pack: 'packs/selected',
        }),
        hasSocialiteProviders() {
            return this.socialiteProviders.length > 0
        },
        isBreakingChange() {
            return process.env.VUE_APP_NEW_SSO === 'true'
        },
    },
    mounted() {
        this.socialiteService.getAvailableProviders(this.params)
            .then((response) => {
                if (this.isBreakingChange) {
                    this.socialiteProviders = response.data.map((item) => item.label);
                } else {
                    this.socialiteProviders = response.data;
                }
            });
    },
    methods: {
        handleSuccess() {
            const user = this.authenticated
            const plan = this.pack

            const response = {
                ...user,
            }

            if (typeof plan !== 'undefined') {
                response.package = plan
            }

            if (typeof AndroidCallbacks !== 'undefined') {
                // eslint-disable-next-line no-undef
                AndroidCallbacks.userLoggedIn(JSON.stringify(response))
            }

            try {
                // eslint-disable-next-line no-undef
                webkit.messageHandlers.loginAction.postMessage(JSON.stringify(response));
            } catch (error) {
                // Not an iOS device
            }

            if (this.previousUrl === null) {
                this.$router.push({ name: 'Continue' })
            } else {
                const routeName = this.previousUrl.name;

                this.$store.dispatch('route/removePreviousUrl')
                this.$router.push({ name: routeName })
            }
        },
        openSingleSignOnWindow(url) {
            const width = 1024;
            const height = 1024;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 4;

            this.ssoWindow = window.open(url, '', `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width= ${width}, height=${height}, top=${top}, left=${left}`);
        },
        providerButtonClass(provider) {
            return `${provider}-sso-btn sso-btn`
        },
        processSocialRequest(provider) {
            this.stopSingleSignOn()

            this.loading = true

            this.socialiteService
                .requestRedirectUrl(this.params, this.device, provider)
                .then((response) => {
                    this.openSingleSignOnWindow(response.data.redirect_uri)

                    this.statusCheck(response.data.verification_uri_complete)
                })
        },
        statusCheck(verificationUri) {
            let result = null

            const check = () => {
                this.socialiteService
                    .status(this.params, verificationUri)
                    .then((response) => {
                        result = response.data
                    })
                    .catch(() => {
                        this.stopSingleSignOn()
                    })
            }

            this.ssoLoop = setInterval(() => {
                check()

                if (result && result.status === 'Completed') {
                    this.stopSingleSignOn()
                    this.ssoWindow.close()

                    if (Object.prototype.hasOwnProperty.call(result, 'token')) {
                        this.$store.dispatch('user/restoreSession', result.token)
                        this.handleSuccess()
                    }
                }
            }, (1000 * 5))

            setTimeout(() => {
                this.stopSingleSignOn()
            }, (1000 * 300))
        },
        stopSingleSignOn() {
            clearInterval(this.ssoLoop)
            this.loading = false
        },
    },
}
</script>

<style scoped>
    .sso-btn {
        @apply text-left relative pl-10;
    }

    .sso-btn:before {
        @apply absolute inline-block w-6 h-6 m-auto inset-y-0 bg-contain bg-no-repeat;
        background-position: left center;
        content: '';
        left: 5px;
    }

    .apple-sso-btn {
        background: #FFFF;
        border: solid #FFFF;
        color: #000 !important;
    }

    .apple-sso-btn:before {
        background-image: url(https://vuejs.simplestreamcdn.com/social-login/apple_sso_icon.svg);
    }

    .facebook-sso-btn {
        background-color: #1877F2;
        border: solid #1877F2;
    }

    .facebook-sso-btn:before {
        background-image: url(https://vuejs.simplestreamcdn.com/social-login/facebook_sso_icon.svg);
    }

    .google-sso-btn {
        background: #FFFF;
        border: solid #FFFF;
        color: #000 !important;
    }

    .google-sso-btn:before {
        background-image: url(https://vuejs.simplestreamcdn.com/social-login/google_sso_icon.svg);
    }
</style>
